/**
 * SessionSync Service
 * Handles session synchronization between tabs using BroadcastChannel
 * Implemented without dependency injection to avoid cycles
 */
SessionSyncService.$inject = [
    '$injector'
];

/* @ngInject */
function SessionSyncService(
    $injector
) {
    var service = this;
    
    // BroadcastChannel for communication between tabs
    var sessionChannel = null;
    var isInitialized = false;
    var currentTabId = null;
    
    // Session event types
    var SESSION_EVENTS = {
        LOGIN: 'session:login',
        LOGOUT: 'session:logout'
    };
    
    /**
     * Get the current tab ID
     */
    function getCurrentTabId() {
        if (!currentTabId) {
            currentTabId = Date.now() + '_' + Math.random();
        }
        return currentTabId;
    }
    
    /**
     * Initialize the session synchronization service
     */
    service.init = function() {
        if (isInitialized) {
            return;
        }
        
        // Check if BroadcastChannel is available
        if (typeof BroadcastChannel === 'undefined') {
            console.warn('SessionSync: BroadcastChannel is not available, using fallback with localStorage events');
            initLocalStorageFallback();
            return;
        }
        
        initBroadcastChannel();
        isInitialized = true;
    };
    
        /**
     * Initialize BroadcastChannel for communication between tabs
     */
    function initBroadcastChannel() {
        try {
            sessionChannel = new BroadcastChannel('atc_session_sync');
            
            // Listen to messages from other tabs
            sessionChannel.onmessage = function(event) {
                handleSessionEvent(event.data);
            };
                        
            // Also initialize localStorage fallback as backup
            initLocalStorageFallback();
        } catch (error) {
            console.error('SessionSync: Error al inicializar BroadcastChannel:', error);
            initLocalStorageFallback();
        }
    }
    
    /**
     * Fallback using localStorage events for browsers that do not support BroadcastChannel
     * Uses the 'storage'
     */
    function initLocalStorageFallback() {
        // Listen for storage events (fires when localStorage is modified by other tabs)
        window.addEventListener('storage', function(event) {
            if (event.key === 'atc_session_event' && event.newValue) {
                try {
                    var sessionEvent = JSON.parse(event.newValue);
                    if (sessionEvent && sessionEvent.timestamp) {
                        // Only process recent events (within last 10 seconds)
                        var now = Date.now();
                        if (now - sessionEvent.timestamp < 10000) {
                            handleSessionEvent(sessionEvent);
                        }
                    }
                } catch (error) {
                    console.error('SessionSync: Error al procesar evento de localStorage:', error);
                }
            }
        });
    }
    
    /**
     * Handles session events received from other tabs
     */
    function handleSessionEvent(eventData) {
        switch (eventData.type) {
            case SESSION_EVENTS.LOGOUT:
                handleLogoutEvent(eventData);
                break;
            case SESSION_EVENTS.LOGIN:
                handleLoginEvent(eventData);
                break;
            default:
                console.warn('SessionSync: Tipo de evento desconocido:', eventData.type);
        }
    }
    
    /**
     * Handles the logout event from another tab
     */
    function handleLogoutEvent(eventData) {
        try {
            // Check if this event was sent by the current tab (to avoid processing own events)
            if (eventData.tabId && eventData.tabId === getCurrentTabId()) {
                return;
            }
            
            var $rootScope = $injector.get('$rootScope');
            var $state = $injector.get('$state');
            var User = $injector.get('User');
            var alerts = $injector.get('alerts');
            var $uibModalStack = $injector.get('$uibModalStack');
            
            // Use the centralized logout function from User service
            User.logout();
            
            // Clean up localStorage
            localStorage.removeItem('session_last');
            
            // Emit event to other services to handle the cleanup
            $rootScope.$broadcast('session:logout:cleanup');
            
            // Close all modals
            $uibModalStack.dismissAll();
            
            // Show message to the user
            alerts.error(
                'Sesión cerrada',
                'Tu sesión ha sido cerrada desde otra pestaña. Serás redirigido al login.',
                {
                    timeout: 2
                }
            );
            
            // Redirect to login after a brief delay
            setTimeout(function() {
                $state.go('login.signin');
            }, 2000);
        } catch (error) {
            console.error('SessionSync: Error al manejar logout:', error);
        }
    }
    
    /**
     * Handles the login event from another tab
     */
    function handleLoginEvent(eventData) {
        try {
            var User = $injector.get('User');
            var $state = $injector.get('$state');
            var alerts = $injector.get('alerts');
            var $rootScope = $injector.get('$rootScope');
            
            // If there is a different user logged in, close the current session
            if (User.loggedIn && User.data.id !== eventData.userId) {
                User.logout();
                localStorage.removeItem('session_last');
                $rootScope.$broadcast('session:logout:cleanup');
                alerts.error(
                    'Cambio de sesión',
                    'Se ha iniciado sesión con un usuario diferente en otra pestaña. Serás redirigido al login.',
                    {
                        timeout: 2
                    }
                );
                setTimeout(function() {
                    $state.go('login.signin');
                }, 2000);
            }
            // If we're on the login page and a login happens in another tab, 
            // we should show a message and refresh the page to show the logged in state
            else if (!User.loggedIn && $state.current.name === 'login.signin') {
                alerts.success(
                    'Sesión iniciada',
                    'Se ha iniciado sesión en otra pestaña. Esta página se actualizará.',
                    {
                        timeout: 2
                    }
                );
                setTimeout(function() {
                    window.location.reload();
                }, 2000);
            }
        } catch (error) {
            console.error('SessionSync: Error al manejar login:', error);
        }
    }
    
    /**
     * Sends a logout event to other tabs
     */
    service.broadcastLogout = function() {
        try {
            var User = $injector.get('User');
            var eventData = {
                type: SESSION_EVENTS.LOGOUT,
                timestamp: Date.now(),
                userId: User.data.id || null
            };
            
            broadcastEvent(eventData);
        } catch (error) {
            console.error('SessionSync: Error al enviar logout:', error);
        }
    };
    
    /**
     * Sends a login event to other tabs
     */
    service.broadcastLogin = function(userId) {
        try {
            var eventData = {
                type: SESSION_EVENTS.LOGIN,
                timestamp: Date.now(),
                userId: userId
            };
            
            broadcastEvent(eventData);
        } catch (error) {
            console.error('SessionSync: Error al enviar login:', error);
        }
    };
    
    /**
     * Sends an event to other tabs
     */
    function broadcastEvent(eventData) {
        // Add current tab ID to the event
        eventData.tabId = getCurrentTabId();
        
        // Always send via BroadcastChannel if available
        if (sessionChannel) {
            sessionChannel.postMessage(eventData);
        }
        
        // Always also send via localStorage as backup
        try {
            localStorage.setItem('atc_session_event', JSON.stringify(eventData));
            // Remove the event after a longer delay to ensure other tabs can read it
            setTimeout(function() {
                localStorage.removeItem('atc_session_event');
            }, 1000); // Increased delay to ensure all tabs can read it
        } catch (error) {
            console.error('SessionSync: Error al enviar evento:', error);
        }
    }
    
    /**
     * Closes the communication channel
     */
    service.destroy = function() {
        if (sessionChannel) {
            sessionChannel.close();
            sessionChannel = null;
        }
        isInitialized = false;
    };
    
    return service;
}

export default SessionSyncService; 